import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { X } from 'lucide-react';
import { useAdminAuditLog } from '@/hooks/api/useAdminApi';

const ACTION_OPTIONS = [
  { value: 'all', label: 'All Actions' },
  { value: 'grant_credits', label: 'Grant Credits' },
  { value: 'adjust_monthly_credits', label: 'Adjust Monthly' },
  { value: 'disable_tenant', label: 'Disable Tenant' },
  { value: 'enable_tenant', label: 'Enable Tenant' },
  { value: 'create_tenant', label: 'Create Tenant' },
  { value: 'send_invite', label: 'Send Invite' },
  { value: 'edit_tenant', label: 'Edit Tenant' },
];

const formatActionName = (action: string): string => {
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getActionBadgeVariant = (action: string) => {
  switch (action) {
    case 'grant_credits':
      return 'default';
    case 'adjust_monthly_credits':
      return 'secondary';
    case 'disable_tenant':
      return 'destructive';
    case 'enable_tenant':
      return 'default';
    case 'create_tenant':
      return 'outline';
    case 'send_invite':
      return 'secondary';
    case 'edit_tenant':
      return 'outline';
    default:
      return 'outline';
  }
};

const getActionBadgeClassName = (action: string): string | undefined => {
  if (action === 'enable_tenant') {
    return 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-100';
  }
  return undefined;
};

const renderDetails = (action: string, details: Record<string, unknown>): string => {
  switch (action) {
    case 'grant_credits':
      return `Granted ${details.amount ?? '?'} addon credits`;

    case 'adjust_monthly_credits':
      return `Adjusted monthly: ${details.previousMonthly ?? '?'} → ${details.newMonthly ?? '?'}`;

    case 'disable_tenant':
      return 'Disabled tenant';

    case 'enable_tenant':
      return 'Enabled tenant';

    case 'create_tenant':
      return details.monthlyCredits
        ? `Created tenant with ${details.monthlyCredits} monthly credits`
        : 'Created tenant';

    case 'send_invite':
      return details.email
        ? `Sent invite to ${details.email}`
        : 'Sent invite';

    case 'edit_tenant': {
      const changes = details.changes as Record<string, unknown> | undefined;
      if (changes && Object.keys(changes).length > 0) {
        return `Edited: ${Object.keys(changes).join(', ')}`;
      }
      return 'Edited tenant';
    }

    default:
      return JSON.stringify(details);
  }
};

const AuditLog = () => {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const limit = 20;

  const { data, isLoading, error } = useAdminAuditLog({
    action: action === 'all' ? undefined : action,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page,
    limit,
  });

  const handleClearFilters = () => {
    setAction('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const hasActiveFilters = action !== 'all' || startDate || endDate;

  const entries = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Audit Log</h2>
        <p className="text-muted-foreground mt-1">
          Track all administrative actions across tenants
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium mb-2 block">Action Type</label>
          <Select value={action} onValueChange={(value) => { setAction(value); setPage(1); }}>
            <SelectTrigger>
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[160px]">
          <label className="text-sm font-medium mb-2 block">Start Date</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          />
        </div>

        <div className="flex-1 min-w-[160px]">
          <label className="text-sm font-medium mb-2 block">End Date</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          />
        </div>

        {hasActiveFilters && (
          <Button variant="outline" onClick={handleClearFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-destructive">
          Failed to load audit log: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Date/Time</TableHead>
              <TableHead className="w-[160px]">Action</TableHead>
              <TableHead>Target Tenant</TableHead>
              <TableHead>Details</TableHead>
              <TableHead className="w-[200px]">Admin</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-64" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                </TableRow>
              ))
            ) : entries.length === 0 ? (
              // Empty state
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  No audit log entries found
                </TableCell>
              </TableRow>
            ) : (
              // Data rows
              entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-mono text-sm">
                    {new Date(entry.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getActionBadgeVariant(entry.action)}
                      className={getActionBadgeClassName(entry.action)}
                    >
                      {formatActionName(entry.action)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {entry.targetTenantName ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {renderDetails(entry.action, entry.details)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {entry.adminUserEmail ?? <span className="text-muted-foreground">System</span>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {meta.page} of {meta.totalPages} ({meta.total} total entries)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={meta.page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={meta.page >= meta.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
